import type { DateTimeRange } from '@/features/events/lib/range.lib';
import type { PageExperience } from '@/features/experiences/types';
import type { TemplateLayout } from '../printableConfigAdapter';
import type { TemplateMeta } from '../types';
import { calendarMeta } from './calendar/meta';
import { dayByCategoryMeta } from './dayByCategory/meta';
import { listVerticalMeta } from './listVertical/meta';
import { plannerCalendarMeta } from './plannerCalendar/meta';
import { plannerMonthMeta } from './plannerMonth/meta';
import { plannerWeekMeta } from './plannerWeek/meta';
import { weekMeta } from './week/meta';
import { yearly3BoxMonthCardMeta } from './yearly3BoxMonthCard/meta';
import { yearly6BoxMonthCardMeta } from './yearly6BoxMonthCard/meta';
import { yearlyMonthCardMeta } from './yearlyMonthCard/meta';

export {
  loadTemplateRenderer,
  type TemplateRendererOptions,
} from './loadTemplateRenderer';

type LayoutKey = TemplateLayout | 'default';

const metas: Record<LayoutKey, TemplateMeta> = {
  default: listVerticalMeta, // backward compatibility
  printableListVertical: listVerticalMeta,
  printableList: listVerticalMeta,
  printableYearlyMonthCard: yearlyMonthCardMeta,
  printableYearly3BoxMonthCard: yearly3BoxMonthCardMeta,
  printableYearly6BoxMonthCard: yearly6BoxMonthCardMeta,
  printableWeek: weekMeta,
  printableCalendar: calendarMeta,
  plannerCalendar: plannerCalendarMeta,
  plannerMonth: plannerMonthMeta,
  plannerWeek: plannerWeekMeta,
  dayByCategory: dayByCategoryMeta,
  // Rendered by CustomArtifactPreview, never through a template; listed only
  // so the Record stays exhaustive.
  printableCustom: listVerticalMeta,
};

export const getTemplateMeta = (layout: TemplateLayout): TemplateMeta =>
  metas[layout] ?? metas.default;

// Whether a layout is bounded to a natural period a range can address (one
// month, one week) — i.e. it declares the period accessors and gets a pager /
// range picker. Layouts without one (the Printable List, the month-card sheets)
// span their whole configured window instead, so a `?range=` in the URL is not
// theirs to consume.
export const isRangeBoundedLayout = (layout: TemplateLayout): boolean => {
  const { getDefaultRange, getRangeForAnchor } = getTemplateMeta(layout);
  return Boolean(getDefaultRange ?? getRangeForAnchor);
};

/**
 * The window a range-bounded layout renders: the layout's natural period
 * containing an explicitly selected range's start, or — with nothing selected —
 * the template's own default period. Returns undefined for a layout that spans
 * its whole configured window, which has no period to resolve.
 *
 * One rule, shared by the views that render a window and the queries that fetch
 * events for one, so a fetch can't be bounded to a window the view then renders
 * past.
 */
export const resolveLayoutRange = (
  layout: TemplateLayout,
  experience: PageExperience,
  selectedRange?: DateTimeRange
): DateTimeRange | undefined => {
  const { getDefaultRange, getRangeForAnchor } = getTemplateMeta(layout);
  if (selectedRange) {
    return (
      getRangeForAnchor?.(selectedRange.startDate, experience) ?? selectedRange
    );
  }
  return getDefaultRange?.(experience);
};
